import moment from 'moment'
import { authenticator } from './authenticator'
import pckg from '../package.json'
import { getChartData } from './lib/groupUnitData'
import {
  buildChartCacheKey,
  getChartCacheMode,
  getChartCacheEntry,
  setChartCacheEntry,
  isChartCacheEntryStale,
  isChartCacheDataTooSparse,
  deleteChartCacheEntry,
  getOrCreateInFlightChartRequest
} from './lib/chartDataCache'
import { debugMessage, cleanup, config } from './common'
import { appendStaffForceSourceQuery } from './lib/staffForceSource'
import { appendStaffViewAsQuery } from './lib/staffViewAs'
import { isLegacyUnitLiveViewResponse } from './lib/legacyP3Unit'

const CLIENT_VERSION = pckg.version

function apiFetch(url, options) {
  return fetch(appendStaffViewAsQuery(appendStaffForceSourceQuery(url)), options)
}

async function loadChartUnitsRawData(idToken, options) {
  const {
    units,
    startUtc,
    endUtc,
    timePreset = '',
    totalsOnly = false,
    liveView = false,
    forceRefresh = false,
    silent = false,
    skipCache = false
  } = options

  const timeFromUtc = startUtc ? startUtc.valueOf() : ''
  const timeToUtc = endUtc ? endUtc.valueOf() : ''
  const cacheMode = getChartCacheMode({ totalsOnly, liveView })
  const useChartCache = !liveView && !skipCache

  function fetchSingleChartDataFromNetwork(unitId) {
    return new Promise((resolve) => {
      const endpoint = totalsOnly
        ? `${API_BASE_URL}/graph/data/totals?uk=1&unitId=${unitId}&timeFromMs=${timeFromUtc}&timeToMs=${timeToUtc}&timePreset=${timePreset}`
        : liveView
        ? `${API_BASE_URL}/graph/data/liveView?uk=1&unitId=${unitId}&timeFromMs=${timeFromUtc}&timeToMs=${timeToUtc}&timePreset=${timePreset}`
        : `${API_BASE_URL}/graph/data?uk=1&unitId=${unitId}&timeFromMs=${timeFromUtc}&timeToMs=${timeToUtc}&timePreset=${timePreset}`

      apiFetch(endpoint, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((response) => response.json())
        .then((response) => {
          if (liveView && isLegacyUnitLiveViewResponse(response)) {
            resolve({ telemetry: null, unitState: null, totals: null })
            return
          }

          if (response?.errorMessage) {
            resolve()
            return
          }

          if (liveView) {
            resolve({ unitState: response?.unitState || null, totals: null, telemetry: null })
            return
          }

          if (totalsOnly) {
            // `/graph/data/totals` is GraphQL `bessEnergyTotals` (mWh). Keep telemetry null.
            resolve({
              telemetry: null,
              totals: response?.totals || null,
              requestedTimeFromMs: response?.requestedTimeFromMs,
              requestedTimeToMs: response?.requestedTimeToMs
            })
            return
          }

          if (Array.isArray(response?.telemetry)) {
            resolve({ telemetry: response.telemetry, totals: response.totals || null })
          } else {
            resolve()
          }
        })
        .catch((err) => {
          if (!silent) ajaxError(err)
          resolve()
        })
    })
  }

  function fetchSingleChartData(hardwareId) {
    const cacheKey = buildChartCacheKey({
      mode: cacheMode,
      hardwareId,
      timeFromMs: timeFromUtc,
      timeToMs: timeToUtc,
      timePreset
    })

    return getOrCreateInFlightChartRequest(cacheKey, async () => {
      let cached = null
      if (useChartCache) {
        cached = await getChartCacheEntry(cacheKey)
        if (!forceRefresh && cached && !isChartCacheEntryStale(cached, timeToUtc)) {
          if (!isChartCacheDataTooSparse(cached.telemetry, { hardwareId })) {
            _debugMessage(`fetchChartData() cache hit ${cacheKey}`)
            return {
              telemetry: cached.telemetry,
              totals: cached.totals,
              requestedTimeFromMs: cached.requestedTimeFromMs,
              requestedTimeToMs: cached.requestedTimeToMs
            }
          }
          await deleteChartCacheEntry(cacheKey)
          _debugMessage(`fetchChartData() evicted sparse cache ${cacheKey}`)
        }
      }

      const result = await fetchSingleChartDataFromNetwork(hardwareId)

      if (useChartCache && result) {
        try {
          await setChartCacheEntry({
            key: cacheKey,
            mode: cacheMode,
            hardwareId,
            timeFromMs: timeFromUtc,
            timeToMs: timeToUtc,
            timePreset,
            telemetry: result.telemetry,
            totals: result.totals,
            requestedTimeFromMs: result.requestedTimeFromMs,
            requestedTimeToMs: result.requestedTimeToMs
          })
        } catch (err) {
          _debugMessage(`fetchChartData() cache write skipped: ${err.message}`)
        }
        return result
      }

      if (useChartCache && cached && !isChartCacheDataTooSparse(cached.telemetry, { hardwareId })) {
        _debugMessage(`fetchChartData() stale cache fallback ${cacheKey}`)
        return {
          telemetry: cached.telemetry,
          totals: cached.totals,
          requestedTimeFromMs: cached.requestedTimeFromMs,
          requestedTimeToMs: cached.requestedTimeToMs
        }
      }

      return result
    })
  }

  let individualUnitsData = []
  for (const unit of units) {
    const { hardwareId } = unit

    const result = (await fetchSingleChartData(hardwareId)) || {}
    const telemetry = Array.isArray(result.telemetry) && result.telemetry.length ? result.telemetry : null
    individualUnitsData = [
      ...individualUnitsData,
      {
        unit,
        telemetry,
        unitState: result.unitState || null,
        totals: result.totals,
        requestedTimeFromMs: result.requestedTimeFromMs,
        requestedTimeToMs: result.requestedTimeToMs
      }
    ]
  }

  return individualUnitsData
}

let API_BASE_URL = 'https://api.portal3.powervault.co.uk'
if (process.env.REACT_APP_STAGE === 'test') API_BASE_URL = 'https://api.test.portal3.powervault.co.uk'
if (process.env.REACT_APP_STAGE === 'staging') API_BASE_URL = 'https://api.staging.portal3.powervault.co.uk'

const _debugMessage = (message) => debugMessage(message, 'ajax.js')

const unitShadowInFlight = new Map()

function sendBmsRecharge(hardwareId, command) {
  _debugMessage(`sendBmsRecharge(${command})`)
  const id = String(hardwareId || '')
  return apiFetch(`${API_BASE_URL}/bmsRecharge?hardwareId=${id}`, {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Api-Key': authenticator.getToken().jwt,
      'client-version': CLIENT_VERSION
    },
    body: JSON.stringify({ command })
  })
    .then((res) => res.json())
    .catch((err) => {
      ajaxError(err)
      throw err
    })
}

// handle 401 Forbidden or ANY other fetch error
function ajaxError(err) {
  // check if local token has expired
  const idToken = authenticator.getToken()

  if (!idToken?.decoded?.exp) {
    // localstorage is broken, so clear cache and redirect to login
    _debugMessage('ajaxError() :: localstorage corrupted. Clearing cache and signing in ...')
    cleanup({ clearCache: true, signOut: true, initiator: 'ajaxError()' })
  } else if (parseInt(moment().utc().format('X'), 10) > idToken.decoded.exp) {
    // token has expired so reinitiate signin (it should happen automatically)
    _debugMessage('ajaxError() :: Token has expired. Signing in ...')
    cleanup({ signIn: true, initiator: 'ajaxError()' })
  } else {
    _debugMessage('ajaxError() :: Failed to fetch')
  }
}

const ajax = {
  fetchVersion: ({ clientVersion }) => {
    _debugMessage('fetchVersion()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/?clientVersion=${clientVersion}`, {
        headers: {
          Accept: 'application/json',
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((versionData) => resolve(versionData))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchUserAuth: (idToken, options = {}) => {
    _debugMessage('fetchUserAuth()')
    const headers = {
      Accept: '*/*',
      'Content-Type': 'application/json',
      'X-Api-Key': idToken.jwt,
      'client-version': CLIENT_VERSION
    }

    const authUrl = (pageNumber) => {
      const params = new URLSearchParams()
      if (pageNumber > 1) params.set('pageNumber', String(pageNumber))
      if (options?.hardwareId) params.set('hardwareId', String(options.hardwareId))
      const query = params.toString()
      return query ? `${API_BASE_URL}/users/auth?${query}` : `${API_BASE_URL}/users/auth`
    }

    const fetchAuthPage = (pageNumber) =>
      apiFetch(authUrl(pageNumber), { method: 'post', headers }).then(async (res) => {
        if (res.status === 401) return { unauthorized: true }
        return res.json()
      })

    return new Promise((resolve, reject) => {
      fetchAuthPage(1)
        .then(async (first) => {
          if (first.unauthorized) {
            resolve(first)
            return
          }
          if (first.errorMessage || !first.data) {
            resolve(first)
            return
          }
          const { data } = first
          const pageCount =
            typeof data.pageCount === 'number' && data.pageCount > 1 ? data.pageCount : 1
          if (pageCount <= 1) {
            resolve(first)
            return
          }

          const extraPages = await Promise.all(
            Array.from({ length: pageCount - 1 }, (_, i) => fetchAuthPage(i + 2))
          )
          for (const pageResult of extraPages) {
            if (pageResult.errorMessage) {
              reject(new Error(pageResult.errorMessage))
              return
            }
          }

          const mergedUnits = [...(data.units || [])]
          for (const pageResult of extraPages) {
            const chunk = pageResult.data?.units
            if (chunk && chunk.length) mergedUnits.push(...chunk)
          }

          resolve({
            ...first,
            data: {
              ...data,
              units: mergedUnits,
              pageNumber: 1,
              pageCount: 1
            }
          })
        })
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  // fetch 5-minutely chart data (Charts/Totals cached in IndexedDB per hardwareId)
  fetchChartData: (idToken, options) => {
    _debugMessage(`fetchChartData()${options?.skipCache ? ' skipCache' : ''}`)

    return new Promise(async (resolve, reject) => {
      try {
        const individualUnitsData = await loadChartUnitsRawData(idToken, options)
        if (options.liveView) {
          resolve({ units: individualUnitsData })
          return
        }
        resolve(getChartData({ unitsData: individualUnitsData }))
      } catch (err) {
        console.error(err)
        ajaxError(err)
        reject(err)
      }
    })
  },

  /** Warm IndexedDB cache for a day/range without updating UI (e.g. adjacent chart days). */
  prefetchChartData: (idToken, options) => {
    _debugMessage('prefetchChartData()')
    return loadChartUnitsRawData(idToken, { ...options, silent: true }).catch(() => {})
  },

  fetchAnnouncements: (idToken) => {
    _debugMessage('fetchAnnouncements()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/announcement`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchWeather: (unitId, idToken) => {
    _debugMessage('fetchWeather()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/weather?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, unitId), {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchTarifflist: (idToken) => {
    _debugMessage('fetchTarifflist()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/tariff`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchSettings: (hardwareId, idToken) => {
    _debugMessage('fetchSettings()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/settings?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, hardwareId), {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  putSettings: (hardwareId, idToken, settingsData) => {
    _debugMessage('putSettings()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/settings?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, hardwareId), {
        method: 'post',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify(settingsData)
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  putCustomName: (hardwareId, idToken, customName) => {
    _debugMessage('putCustomName()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/customName?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, hardwareId), {
        method: 'post',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        },
        body: customName
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchUnitSchedules: (unitId, idToken) => {
    _debugMessage('fetchUnitSchedules()')
    return new Promise((resolve, reject) => {
      const unitScheduleUrl = `${API_BASE_URL}/schedule?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, unitId)

      apiFetch(unitScheduleUrl, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((scheduleData) => resolve(scheduleData))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  putUnitSchedule: (hardwareId, idToken, type, scheduleData) => {
    _debugMessage('putUnitSchedule()')
    return new Promise((resolve, reject) => {
      const unitScheduleUrl = `${API_BASE_URL}/schedule?unitId=%UNIT_ID%`.replace(/%UNIT_ID%/gi, hardwareId)

      apiFetch(unitScheduleUrl, {
        method: 'post',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify({
          type,
          schedule: scheduleData
        })
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchRaw: (idToken, path) => {
    _debugMessage('fetchRaw()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/${path}`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  putRaw: (idToken, path, data) => {
    _debugMessage('putRaw()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/${path}`, {
        method: 'post',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify(data)
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchStateOverrides: (idToken, hardwareId) => {
    _debugMessage('fetchStateOverrides()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/stateOverride?hardwareId=${hardwareId}`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((data) => resolve(data))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  startBmsRecharge: (hardwareId) => sendBmsRecharge(hardwareId, 'start'),

  abortBmsRecharge: (hardwareId) => sendBmsRecharge(hardwareId, 'abort'),

  putStateOverride: (idToken, hardwareId, stateOverrideData) => {
    _debugMessage('putStateOverride()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/stateOverride?hardwareId=${hardwareId}`, {
        method: 'post',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'X-Api-Key': idToken.jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify({ stateOverride: [stateOverrideData] })
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  fetchUnitShadow: (hardwareId) => {
    _debugMessage('fetchUnitShadow()')
    const id = String(hardwareId || '')
    if (!id) return Promise.resolve({ result: 'error', errorMessage: 'Missing hardwareId' })
    if (unitShadowInFlight.has(id)) return unitShadowInFlight.get(id)

    const request = apiFetch(`${API_BASE_URL}/unitShadow?hardwareId=${id}`, {
      headers: {
        Accept: 'application/json',
        'X-Api-Key': authenticator.getToken().jwt,
        'client-version': CLIENT_VERSION
      }
    })
      .then((res) => res.json())
      .catch((err) => {
        ajaxError(err)
        throw err
      })
      .finally(() => {
        unitShadowInFlight.delete(id)
      })

    unitShadowInFlight.set(id, request)
    return request
  },

  // NEW STYLE FETCH
  //  TODO: refactor all ajax requests to use this

  rawRequest: ({ method = 'get', path, queryParams = {}, headers = {}, accept = 'application/json' }) => {
    _debugMessage('fetchRaw()')
    return new Promise((resolve, reject) => {
      let _path = path.substring(0, 1) === '/' ? path.substring(1) : path // remove preceeding slash if present
      if (queryParams) _path = _path + '?' + new URLSearchParams(queryParams).toString()

      apiFetch(`${API_BASE_URL}/${_path}`, {
        method,
        headers: {
          Accept: accept, // '*/*' | 'application/json'
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION,
          ...headers
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  getPreferences: ({ customerAccountId = undefined, configId = undefined, email = undefined, hardwareId = undefined } = {}) => {
    _debugMessage('getPreferences()')
    return new Promise((resolve, reject) => {
      const path =
        'preferences?' +
        new URLSearchParams(
          Object.fromEntries(
            Object.entries({ customerAccountId, configId, email, hardwareId }).filter(([_, value]) => value !== undefined && value !== null && value !== '')
          )
        ).toString()

      apiFetch(`${API_BASE_URL}/${path}`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result?.preferences))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  getSchedulePresets: ({ email } = {}) => {
    _debugMessage('getSchedulePresets()')
    return new Promise((resolve, reject) => {
      if (!email) return reject(new Error('Missing email'))
      const path = 'schedulePresets?' + new URLSearchParams({ email }).toString()
      apiFetch(`${API_BASE_URL}/${path}`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => {
          if (result?.errorMessage) return reject(new Error(result.errorMessage))
          resolve(Array.isArray(result?.schedulePresets) ? result.schedulePresets : [])
        })
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  postSchedulePresets: ({ email } = {}, { schedulePresets } = {}) => {
    _debugMessage('postSchedulePresets()')
    return new Promise((resolve, reject) => {
      if (!email) return reject(new Error('Missing email'))
      const path = 'schedulePresets?' + new URLSearchParams({ email }).toString()
      apiFetch(`${API_BASE_URL}/${path}`, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify({ schedulePresets })
      })
        .then((res) => res.json())
        .then((result) => {
          if (result?.errorMessage) return reject(new Error(result.errorMessage))
          resolve(Array.isArray(result?.schedulePresets) ? result.schedulePresets : [])
        })
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  postPreferences: ({ customerAccountId = undefined, configId = undefined, email = undefined, hardwareId = undefined } = {}, preferences = {}) => {
    _debugMessage('setPreferences()')
    return new Promise((resolve, reject) => {
      if (typeof preferences !== 'object') return reject('`preferences` must be an object')

      const path =
        'preferences?' +
        new URLSearchParams(
          Object.fromEntries(
            Object.entries({ customerAccountId, configId, email, hardwareId }).filter(([_, value]) => value !== undefined && value !== null && value !== '')
          )
        ).toString()

      apiFetch(`${API_BASE_URL}/${path}`, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify({ preferences })
      })
        .then((res) => res.json())
        .then((result) => resolve(result?.preferences))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  getStaffPreference: () => {
    _debugMessage('getStaffPreference()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/staffPreference`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  putStaffPreference: ({ starredHardwareIds, starredLabels } = {}) => {
    _debugMessage('putStaffPreference()')
    return new Promise((resolve, reject) => {
      apiFetch(`${API_BASE_URL}/staffPreference`, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        },
        body: JSON.stringify({ starredHardwareIds, starredLabels })
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  },

  searchUnits: ({ q, limit = 10, cursor } = {}) => {
    _debugMessage('searchUnits()')
    return new Promise((resolve, reject) => {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (limit) params.set('limit', String(limit))
      if (cursor) params.set('cursor', cursor)
      apiFetch(`${API_BASE_URL}/units/search?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Key': authenticator.getToken().jwt,
          'client-version': CLIENT_VERSION
        }
      })
        .then((res) => res.json())
        .then((result) => resolve(result))
        .catch((err) => {
          ajaxError(err)
          reject(err)
        })
    })
  }
}

export { ajax }
